import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, ShieldCheck, Zap, RefreshCw, Copy, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TRIGGER = "bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-lg";

function ProviderBadge({ ready, label, code }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${ready ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-neutral-500/15 text-neutral-300 border border-neutral-500/30"}`}
      data-testid={`pg-provider-${code?.toLowerCase()}`}>
      {label}{!ready ? " · STUB" : ""}
    </span>
  );
}

function StatusChip({ status }) {
  const key = String(status || "INACTIVE").toUpperCase();
  const map = {
    ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    INACTIVE: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    ERROR: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    SUCCESS: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    PENDING: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    FAILED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    EXPIRED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    CANCELLED: "bg-neutral-500/15 text-neutral-300 border-neutral-500/30",
    REFUNDED: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    PROCESSED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    RECEIVED: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    DUPLICATE: "bg-neutral-500/15 text-neutral-300 border-neutral-500/30",
    REJECTED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    PARTIAL_SUCCESS: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${map[key] || map.INACTIVE}`}>{key}</span>;
}

function ConfigForm({ providers, activeConfig, onSaved }) {
  const [provider, setProvider] = useState(activeConfig?.provider || "MIDTRANS");
  const [environment, setEnvironment] = useState(activeConfig?.environment || "SANDBOX");
  const [status, setStatus] = useState(activeConfig?.status || "INACTIVE");
  const [callbackUrl, setCallbackUrl] = useState(activeConfig?.callback_url || "");
  const [webhookUrl, setWebhookUrl] = useState(activeConfig?.webhook_url || "/api/payment/webhook/midtrans");
  const [merchantId, setMerchantId] = useState("");
  const [serverKey, setServerKey] = useState("");
  const [clientKey, setClientKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (activeConfig) {
      setProvider(activeConfig.provider);
      setEnvironment(activeConfig.environment || "SANDBOX");
      setStatus(activeConfig.status || "INACTIVE");
      setCallbackUrl(activeConfig.callback_url || "");
      setWebhookUrl(activeConfig.webhook_url || "/api/payment/webhook/midtrans");
    }
  }, [activeConfig]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        provider, environment, status,
        callback_url: callbackUrl || null,
        webhook_url: webhookUrl || null,
      };
      // Write-only fields — send only if user typed
      if (merchantId) payload.merchant_id_value = merchantId;
      if (serverKey) payload.server_key_value = serverKey;
      if (clientKey) payload.client_key_value = clientKey;
      if (webhookSecret) payload.webhook_secret_value = webhookSecret;
      await api.post("/platform/payment-gateways/config", payload);
      toast.success("Gateway config disimpan.");
      setMerchantId(""); setServerKey(""); setClientKey(""); setWebhookSecret("");
      onSaved?.();
    } catch (e) { notifyError(e, "Gagal simpan config"); }
    finally { setBusy(false); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const r = await api.post("/platform/payment-gateways/test", { provider });
      const ok = r.data?.result?.ok;
      toast[ok ? "success" : "error"](`Test connection: ${ok ? "OK" : "FAILED"} (${r.data?.result?.reason || r.data?.result?.mode})`);
      onSaved?.();
    } catch (e) { notifyError(e, "Test gagal"); }
    finally { setTesting(false); }
  };

  const fld = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/60";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="pg-config-card">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={16} className="text-emerald-300" />
        <div className="text-white font-display font-black text-sm">Payment Gateway Settings</div>
        <div className="ml-auto flex items-center gap-2">
          {providers?.map(p => <ProviderBadge key={p.code} code={p.code} label={p.label} ready={p.ready} />)}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Provider</label>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} className={fld} data-testid="pg-cfg-provider">
            {providers?.map(p => <option key={p.code} value={p.code}>{p.label}{!p.ready ? " (stub)" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Environment</label>
          <select value={environment} onChange={(e) => setEnvironment(e.target.value)} className={fld} data-testid="pg-cfg-env">
            <option value="SANDBOX">SANDBOX</option>
            <option value="PRODUCTION">PRODUCTION</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={fld} data-testid="pg-cfg-status">
            <option value="INACTIVE">INACTIVE</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="ERROR">ERROR</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Callback URL</label>
          <input value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)} className={fld} data-testid="pg-cfg-callback" placeholder="https://yourapp.com/payment/return" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Webhook URL</label>
          <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} className={fld} data-testid="pg-cfg-webhook" placeholder="/api/payment/webhook/midtrans" />
        </div>
      </div>

      <div className="mt-4 border-t border-white/10 pt-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">
          <AlertTriangle size={12} className="inline mr-1" /> Secret Inputs (Write-Only — value lama TIDAK ditampilkan)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: "merchant_id", value: merchantId, setter: setMerchantId, refKey: "merchant_id_ref", placeholder: "Merchant ID" },
            { key: "server_key", value: serverKey, setter: setServerKey, refKey: "server_key_ref", placeholder: "Server Key (SB-Mid-server-…)" },
            { key: "client_key", value: clientKey, setter: setClientKey, refKey: "client_key_ref", placeholder: "Client Key (SB-Mid-client-…)" },
            { key: "webhook_secret", value: webhookSecret, setter: setWebhookSecret, refKey: "webhook_secret_ref", placeholder: "Webhook Secret" },
          ].map(f => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{f.placeholder}</label>
              <input type="password" autoComplete="new-password" value={f.value} onChange={(e) => f.setter(e.target.value)} className={fld}
                placeholder={activeConfig?.[f.refKey] ? `••• (ref: ${activeConfig[f.refKey].slice(0, 12)}…)` : f.placeholder}
                data-testid={`pg-cfg-${f.key.replace('_', '-')}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-[10px] text-white/50">
        <div><span className="uppercase tracking-widest font-bold">Last Tested</span>: {activeConfig?.last_tested_at || "—"}</div>
        <div><span className="uppercase tracking-widest font-bold">Last Error</span>: {activeConfig?.last_error || "—"}</div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button onClick={testConn} disabled={testing || busy} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="pg-cfg-test">
          <Zap size={14} className="mr-1" /> {testing ? "Testing…" : "Test Connection"}
        </Button>
        <Button onClick={save} disabled={busy} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white font-black" data-testid="pg-cfg-save">
          {busy ? "Menyimpan…" : "Simpan Config"}
        </Button>
      </div>
    </div>
  );
}

function CheckoutAction({ invoice, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/payment-gateways/invoices/${invoice.invoice_id}/checkout`, {});
      setResult(r.data);
      toast.success(`Checkout dibuat (${r.data.mode}).`);
      onSuccess?.();
    } catch (e) { notifyError(e, "Gagal checkout"); }
    finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Disalin."); };
  return (
    <div className="flex flex-col gap-1" data-testid={`pg-checkout-${invoice.invoice_id}`}>
      <Button size="sm" onClick={start} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs" data-testid={`pg-checkout-btn-${invoice.invoice_id}`}>
        <CreditCard size={12} className="mr-1" /> Create Checkout
      </Button>
      {result?.redirect_url && (
        <div className="flex items-center gap-1 text-[10px] text-emerald-300">
          <a href={result.redirect_url} target="_blank" rel="noreferrer" className="underline truncate max-w-[140px]" data-testid={`pg-pay-url-${invoice.invoice_id}`}>
            {result.redirect_url}
          </a>
          <Button size="sm" variant="ghost" onClick={() => copy(result.redirect_url)} className="text-white/60 hover:bg-white/10 h-6 w-6 p-0"><Copy size={10} /></Button>
        </div>
      )}
      {result && !result.redirect_url && (
        <div className="text-[10px] text-white/50" data-testid={`pg-checkout-mode-${invoice.invoice_id}`}>Mode: {result.mode}{result.reason ? ` — ${result.reason}` : ""}</div>
      )}
    </div>
  );
}

export default function PaymentGatewayTab({ tab }) {
  const [providers, setProviders] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gw, sm, ev, inv] = await Promise.all([
        api.get("/platform/payment-gateways"),
        api.get("/platform/payment-gateways/summary"),
        api.get("/platform/payment-gateways/events", { params: { limit: 50 } }),
        api.get("/platform/billing/invoices", { params: { limit: 20, status: "PENDING" } }),
      ]);
      setProviders(gw.data.providers || []);
      setGateways(gw.data.items || []);
      setSummary(sm.data);
      setEvents(ev.data.items || []);
      setInvoices(inv.data.items || []);
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tab === "gateway") load(); }, [tab, load]);

  if (tab !== "gateway") return null;

  const activeConfig = gateways.find(g => g.status === "ACTIVE") || gateways[0];

  return (
    <div className="space-y-5" data-testid="pg-tab-content">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pg-summary-cards">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4"><div className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold">Gateway Status</div><div className="text-lg font-black text-emerald-200 mt-1">{summary.gateway_status}</div></div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Successful</div><div className="text-2xl font-black text-emerald-300 mt-1">{summary.successful_payments}</div></div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Pending</div><div className="text-2xl font-black text-amber-300 mt-1">{summary.pending_payments}</div></div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Failed</div><div className="text-2xl font-black text-rose-300 mt-1">{summary.failed_payments}</div></div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Refunded</div><div className="text-2xl font-black text-violet-300 mt-1">{summary.refunded_payments}</div></div>
        </div>
      )}

      <ConfigForm providers={providers} activeConfig={activeConfig} onSaved={load} />

      {/* Invoice quick checkout */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="pg-invoices-checkout">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard size={14} className="text-emerald-300" />
          <div className="text-white font-black text-sm">Pending Invoices — Quick Checkout</div>
          <Button variant="ghost" onClick={load} className="ml-auto text-white/60 hover:bg-white/10" data-testid="pg-refresh">
            <RefreshCw size={12} className="mr-1" /> Reload
          </Button>
        </div>
        {loading ? <div className="text-white/40 text-sm">Memuat…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10">
                  <th className="py-2 font-bold">Invoice</th>
                  <th className="py-2 font-bold">Tenant</th>
                  <th className="py-2 font-bold">Total</th>
                  <th className="py-2 font-bold">Due</th>
                  <th className="py-2 font-bold">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && <tr><td colSpan={5} className="py-4 text-white/40 text-center" data-testid="pg-invoices-empty">Tidak ada invoice PENDING.</td></tr>}
                {invoices.map(inv => (
                  <tr key={inv.invoice_id} className="border-b border-white/5" data-testid={`pg-inv-row-${inv.invoice_id}`}>
                    <td className="py-2 font-mono text-white">{inv.invoice_number}</td>
                    <td className="py-2 text-white/80">{inv.tenant_name || inv.tenant_slug}</td>
                    <td className="py-2 font-mono text-white">Rp {Number(inv.grand_total || 0).toLocaleString("id-ID")}</td>
                    <td className="py-2 text-white/60">{inv.due_date?.slice(0, 10)}</td>
                    <td className="py-2"><CheckoutAction invoice={inv} onSuccess={load} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Events */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="pg-events-table">
        <div className="text-white font-black text-sm mb-3">Payment Events</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10">
                <th className="py-2 font-bold">Provider</th>
                <th className="py-2 font-bold">Event</th>
                <th className="py-2 font-bold">Invoice</th>
                <th className="py-2 font-bold">Transaction</th>
                <th className="py-2 font-bold">Payment</th>
                <th className="py-2 font-bold">Signature</th>
                <th className="py-2 font-bold">Processed</th>
                <th className="py-2 font-bold">Date</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && <tr><td colSpan={8} className="py-4 text-white/40 text-center" data-testid="pg-events-empty">Belum ada event.</td></tr>}
              {events.map(e => (
                <tr key={e.id} className="border-b border-white/5" data-testid={`pg-event-row-${e.id}`}>
                  <td className="py-2 font-mono text-cyan-300">{e.provider}</td>
                  <td className="py-2 text-white/80 uppercase text-[10px] font-bold">{e.event_type}</td>
                  <td className="py-2 font-mono text-white/60 truncate max-w-[100px]">{e.invoice_id?.slice(0, 12) || "—"}</td>
                  <td className="py-2 font-mono text-white/60 truncate max-w-[120px]">{e.transaction_id?.slice(0, 16)}</td>
                  <td className="py-2"><StatusChip status={e.payment_status} /></td>
                  <td className="py-2">{e.signature_valid ? <CheckCircle2 size={12} className="text-emerald-400" /> : <AlertTriangle size={12} className="text-amber-400" />}</td>
                  <td className="py-2"><StatusChip status={e.status} /></td>
                  <td className="py-2 text-white/60">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
